import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ErrorApi } from '../../api/cliente';
import type { TipoEvento, TipoEventoEntrada } from '../../api/tipos';
import { EsqueletoListaAdmin } from '../../componentes/Esqueletos';
import { Aviso } from '../../componentes/Estados';
import { COLORES_SUGERIDOS } from './constantes';

interface Borrador {
  id: string | null;
  nombre: string;
  color: string;
  activo: boolean;
}

const borradorNuevo = (): Borrador => ({
  id: null,
  nombre: '',
  color: COLORES_SUGERIDOS[0] ?? '#A1C623',
  activo: true,
});

export function PanelTiposEvento({ onCerrar }: { onCerrar: () => void }) {
  const clienteConsultas = useQueryClient();
  const [borrador, setBorrador] = useState<Borrador>(borradorNuevo);
  const [porBorrar, setPorBorrar] = useState<string | null>(null);

  const tipos = useQuery({
    queryKey: ['admin', 'tipos-evento'],
    queryFn: () => api.get<TipoEvento[]>('/admin/tipos-evento'),
  });

  const refrescar = () => {
    void clienteConsultas.invalidateQueries({ queryKey: ['admin', 'tipos-evento'] });
    void clienteConsultas.invalidateQueries({ queryKey: ['tipos-evento'] });
    void clienteConsultas.invalidateQueries({ queryKey: ['eventos'] });
    void clienteConsultas.invalidateQueries({ queryKey: ['admin', 'eventos'] });
  };

  const guardar = useMutation({
    mutationFn: (datos: Borrador) => {
      const cuerpo: TipoEventoEntrada = {
        nombre: datos.nombre.trim(),
        color: datos.color,
        orden: 0,
        activo: datos.activo,
      };
      return datos.id
        ? api.put<TipoEvento>(`/admin/tipos-evento/${datos.id}`, cuerpo)
        : api.post<TipoEvento>('/admin/tipos-evento', cuerpo);
    },
    onSuccess: () => {
      setBorrador(borradorNuevo());
      refrescar();
    },
  });

  const eliminar = useMutation({
    mutationFn: (id: string) => api.borrar(`/admin/tipos-evento/${id}`),
    onSuccess: () => {
      setPorBorrar(null);
      refrescar();
    },
  });

  const error =
    guardar.error instanceof ErrorApi
      ? guardar.error
      : eliminar.error instanceof ErrorApi
        ? eliminar.error
        : null;

  return (
    <div
      className="fixed inset-0 z-[60] overflow-y-auto bg-[rgba(8,15,26,.72)] px-4 py-8 sm:px-5 sm:py-12"
      onClick={onCerrar}
      role="presentation"
    >
      <div
        className="mx-auto w-full max-w-[560px] animate-aparecer overflow-hidden rounded-2xl border border-white/[.12] bg-panel shadow-modal"
        onClick={(evento) => evento.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Tipos de evento"
      >
        <div className="flex items-center gap-3 border-b border-white/[.08] px-5 py-4 sm:px-6">
          <div className="min-w-0 flex-1">
            <h2 className="font-titulo text-[19px] font-extrabold">Tipos de evento</h2>
            <p className="mt-0.5 text-xs text-debil">
              El color pinta la barra y el chip del evento en toda la app.
            </p>
          </div>
          <button
            type="button"
            onClick={onCerrar}
            aria-label="Cerrar"
            className="h-8 w-8 flex-none rounded-[9px] border border-white/15 text-sm leading-none text-tenue transition-colors hover:bg-white/5"
          >
            ×
          </button>
        </div>

        <div className="border-b border-white/[.08] p-5 sm:p-6">
          <div className="flex flex-wrap items-end gap-3">
            <div className="min-w-[180px] flex-1">
              <label className="etiqueta" htmlFor="nombre-tipo">
                {borrador.id ? 'Editando tipo' : 'Nuevo tipo'}
              </label>
              <input
                id="nombre-tipo"
                type="text"
                className="campo"
                maxLength={60}
                placeholder="Ej: After, Cumpleaños, Workshop…"
                value={borrador.nombre}
                onChange={(e) => setBorrador({ ...borrador, nombre: e.target.value })}
              />
            </div>

            <button
              type="button"
              disabled={!borrador.nombre.trim() || guardar.isPending}
              onClick={() => guardar.mutate(borrador)}
              className="rounded-[9px] bg-lima px-4 py-2.5 font-titulo text-[13px] font-bold text-tinta transition-colors hover:bg-lima-claro disabled:opacity-50"
            >
              {guardar.isPending ? 'Guardando…' : borrador.id ? 'Guardar' : 'Agregar'}
            </button>

            {borrador.id && (
              <button
                type="button"
                onClick={() => setBorrador(borradorNuevo())}
                className="rounded-[9px] border border-white/15 px-4 py-2.5 text-[13px] text-tenue transition-colors hover:bg-white/5"
              >
                Cancelar
              </button>
            )}
          </div>

          <div className="mt-3.5 flex flex-wrap items-center gap-2">
            {COLORES_SUGERIDOS.map((color) => (
              <button
                key={color}
                type="button"
                aria-label={`Color ${color}`}
                onClick={() => setBorrador({ ...borrador, color })}
                className={`h-7 w-7 rounded-full transition-transform ${
                  borrador.color.toLowerCase() === color.toLowerCase()
                    ? 'scale-110 ring-2 ring-white/70'
                    : 'hover:scale-105'
                }`}
                style={{ background: color }}
              />
            ))}
            <input
              type="color"
              aria-label="Color personalizado"
              className="h-7 w-10 cursor-pointer rounded border border-white/15 bg-transparent"
              value={borrador.color}
              onChange={(e) => setBorrador({ ...borrador, color: e.target.value })}
            />
          </div>

          {error && (
            <div className="mt-3.5">
              <Aviso tono="error">{error.message}</Aviso>
            </div>
          )}
        </div>

        <div className="p-5 sm:p-6">
          {tipos.isPending ? (
            <EsqueletoListaAdmin cantidad={3} />
          ) : tipos.data && tipos.data.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {tipos.data.map((tipo) => (
                <li
                  key={tipo.id}
                  className="flex flex-wrap items-center gap-3 rounded-[10px] border border-white/[.07] bg-white/[.04] px-3 py-2.5"
                >
                  <span
                    className="h-4 w-4 flex-none rounded-full"
                    style={{ background: tipo.color }}
                  />
                  <span className="min-w-0 flex-1 truncate text-[13.5px] text-titulo">
                    {tipo.nombre}
                  </span>
                  <span className="text-[11.5px] text-mudo">
                    {tipo.eventos === 0
                      ? 'sin eventos'
                      : `${tipo.eventos} ${tipo.eventos === 1 ? 'evento' : 'eventos'}`}
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      setBorrador({
                        id: tipo.id,
                        nombre: tipo.nombre,
                        color: tipo.color,
                        activo: tipo.activo,
                      })
                    }
                    className="rounded-lg border border-white/15 px-2.5 py-1 text-xs text-tenue transition-colors hover:bg-white/5"
                  >
                    Editar
                  </button>

                  {porBorrar === tipo.id ? (
                    <span className="flex items-center gap-2">
                      <button
                        type="button"
                        disabled={eliminar.isPending}
                        onClick={() => eliminar.mutate(tipo.id)}
                        className="rounded-lg bg-coral px-2.5 py-1 text-xs font-bold text-tinta disabled:opacity-60"
                      >
                        Confirmar
                      </button>
                      <button
                        type="button"
                        onClick={() => setPorBorrar(null)}
                        className="rounded-lg border border-white/20 px-2.5 py-1 text-xs text-suave"
                      >
                        No
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => setPorBorrar(tipo.id)}
                      className="rounded-lg border border-coral/30 px-2.5 py-1 text-xs text-coral transition-colors hover:bg-coral/10"
                    >
                      Borrar
                    </button>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-[13px] text-mudo">
              Todavía no hay tipos cargados. Creá el primero acá arriba.
            </p>
          )}

          {porBorrar && (
            <p className="mt-3.5 text-xs text-mudo">
              Borrar un tipo no borra sus eventos: quedan sin tipo y se les puede asignar otro.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
